import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Button from '@hellodarwin/core/lib/components/common/button';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import { usePlatform } from '@hellodarwin/core/lib/features/providers/platform-provider';
import Arrow from '@hellodarwin/icons/dist/icons/Arrow';
import Delete from '@hellodarwin/icons/dist/icons/Delete';
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import getTestId from '../../../../tests/utils/get-test-id';
import { ALL_MODEL_LABELS, AssistantStatus, getDefaultModel, } from '../../constants/assistant.constants';
import { useAssistantContext } from '../../providers';
import { clearConversationProcessingNotice, selectAssistantStatus, selectModelAutoSwitch, setAssistantStatus, updateConversationActivity, } from '../../redux';
import { useAssistantDispatch, useAssistantSelector, } from '../../redux/assistant-hooks';
import { addMessage, removeMessage } from '../../redux/assistantMessageSlice';
import { clearMessageStreamingError, clearModelAutoSwitch, setMessageStreamingError, } from '../../redux/assistantSlice';
import { ActorType } from '../../types/conversation.types';
import { MessageRole } from '../../types/message.types';
import { AgentModeSelector, ModelSelector } from '../common';
import { AssistantChatInputComposerControls, AssistantChatInputQueue, AssistantChatInputQueueHeader, AssistantChatInputQueueMessageActions, ChatBackground, ChatInputContainer, ChatInputWrapper, InputWrapper, QueueContainer, QueueMessageCard, QueueMessageContent, StyledTextArea, } from './styles';
const AssistantChatInput = ({ conversationId, disabled = false, placeholder, initialMessage = '', hasConversation, is_onboarding, hasNoMessages, }) => {
    const theme = useTheme();
    const { app } = usePlatform();
    const { createConversation: onSendWithoutConversation, sendMessage: sendMessageViaSSE, setIsAutoScrollEnabled, agentMode, setAgentMode, usageContext, eventProcessor, } = useAssistantContext();
    const { t } = useTranslation();
    const dispatch = useAssistantDispatch();
    const [message, setMessage] = useState(initialMessage);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [queuedMessages, setQueuedMessages] = useState([]);
    const isAdmin = app === 'admin';
    const [selectedModel, setSelectedModel] = useState(getDefaultModel(app));
    const modelToUse = isAdmin ? selectedModel : getDefaultModel(app);
    const status = useAssistantSelector(selectAssistantStatus);
    const modelAutoSwitch = useAssistantSelector(selectModelAutoSwitch);
    useEffect(() => {
        if (!modelAutoSwitch) {
            return;
        }
        if (modelAutoSwitch.toModel &&
            modelAutoSwitch.toModel in ALL_MODEL_LABELS) {
            setSelectedModel(modelAutoSwitch.toModel);
        }
    }, [modelAutoSwitch]);
    const modelAutoSwitchNotice = useMemo(() => {
        var _a, _b;
        if (!modelAutoSwitch) {
            return null;
        }
        const fromLabel = (_a = ALL_MODEL_LABELS[modelAutoSwitch.fromModel]) !== null && _a !== void 0 ? _a : modelAutoSwitch.fromModel;
        const toLabel = (_b = ALL_MODEL_LABELS[modelAutoSwitch.toModel]) !== null && _b !== void 0 ? _b : modelAutoSwitch.toModel;
        return t('assistant|model_switched_provider_error', {
            fromModel: fromLabel,
            toModel: toLabel,
        });
    }, [modelAutoSwitch, t]);
    const textAreaRef = useRef(null);
    const currentConversationIdRef = useRef(conversationId);
    const previousConversationIdRef = useRef(conversationId);
    const queueDrainInFlightRef = useRef(null);
    const queueScopeVersionRef = useRef(0);
    const lastSubmittedRef = useRef(null);
    const resetTextAreaHeight = useCallback(() => {
        var _a, _b;
        const textArea = (_b = (_a = textAreaRef.current) === null || _a === void 0 ? void 0 : _a.resizableTextArea) === null || _b === void 0 ? void 0 : _b.textArea;
        if (!textArea)
            return;
        textArea.style.height = '0px';
        textArea.style.overflowY = 'hidden';
        window.requestAnimationFrame(() => {
            textArea.style.height = 'auto';
            textArea.style.overflowY = 'hidden';
        });
    }, []);
    const defaultPlaceholder = conversationId
        ? t('assistant|input_placeholder', 'Type your message...')
        : t('assistant|input_placeholder_start', 'Type a message to start...');
    useEffect(() => {
        const previousConversationId = previousConversationIdRef.current;
        const hasSwitchedConversation = !!previousConversationId &&
            !!conversationId &&
            previousConversationId !== conversationId;
        const hasExitedConversation = !!previousConversationId && !conversationId;
        if (hasSwitchedConversation || hasExitedConversation) {
            queueScopeVersionRef.current += 1;
            setQueuedMessages([]);
        }
        if (conversationId) {
            currentConversationIdRef.current = conversationId;
        }
        else if (!isSubmitting) {
            currentConversationIdRef.current = null;
        }
        previousConversationIdRef.current = conversationId;
    }, [conversationId, isSubmitting]);
    const assistantIsBusy = useMemo(() => isSubmitting ||
        [
            AssistantStatus.THINKING,
            AssistantStatus.TOOL_EXECUTING,
            AssistantStatus.RESPONDING,
        ].includes(status), [isSubmitting, status]);
    const ensureConversationId = useCallback(async () => {
        var _a, _b;
        const existingConversationId = (_a = currentConversationIdRef.current) !== null && _a !== void 0 ? _a : conversationId;
        if (existingConversationId) {
            return existingConversationId;
        }
        if (!onSendWithoutConversation) {
            return null;
        }
        const conversation = await onSendWithoutConversation();
        const nextConversationId = (_b = conversation === null || conversation === void 0 ? void 0 : conversation.id) !== null && _b !== void 0 ? _b : null;
        if (nextConversationId) {
            currentConversationIdRef.current = nextConversationId;
        }
        return nextConversationId;
    }, [conversationId, onSendWithoutConversation]);
    const submitAssistantMessage = useCallback(async (trimmedMessage, targetConversationId) => {
        if (!trimmedMessage || !targetConversationId || !sendMessageViaSSE) {
            return false;
        }
        setIsSubmitting(true);
        dispatch(clearConversationProcessingNotice(targetConversationId));
        dispatch(setAssistantStatus(AssistantStatus.PROCESSING));
        dispatch(clearMessageStreamingError());
        const tempUserMessage = {
            id: `tmp_${Date.now()}`,
            conversation_id: targetConversationId,
            root_conversation_id: targetConversationId,
            content: trimmedMessage,
            role: MessageRole.USER,
            author_id: '',
            author_type: app === 'client' ? ActorType.CLIENT : ActorType.ADMIN,
            timestamp: new Date().toISOString(),
        };
        dispatch(addMessage(tempUserMessage));
        lastSubmittedRef.current = {
            text: trimmedMessage,
            tempId: tempUserMessage.id,
            conversationId: targetConversationId,
        };
        dispatch(updateConversationActivity(targetConversationId));
        try {
            await sendMessageViaSSE(trimmedMessage, targetConversationId, modelToUse, is_onboarding);
            resetTextAreaHeight();
            return true;
        }
        catch (error) {
            dispatch(setAssistantStatus(AssistantStatus.IDLE));
            console.error('Failed to send message:', error);
            return false;
        }
        finally {
            lastSubmittedRef.current = null;
            setIsSubmitting(false);
        }
    }, [
        app,
        dispatch,
        is_onboarding,
        modelToUse,
        resetTextAreaHeight,
        sendMessageViaSSE,
    ]);
    const handleSend = useCallback(async () => {
        const trimmedMessage = message.trim();
        if (!trimmedMessage || assistantIsBusy || disabled) {
            return;
        }
        setMessage('');
        resetTextAreaHeight();
        const currentConversationId = await ensureConversationId();
        await submitAssistantMessage(trimmedMessage, currentConversationId);
    }, [
        assistantIsBusy,
        disabled,
        ensureConversationId,
        message,
        resetTextAreaHeight,
        submitAssistantMessage,
    ]);
    const handleQueueMessage = useCallback(() => {
        var _a;
        const trimmedMessage = message.trim();
        if (!trimmedMessage || disabled) {
            return;
        }
        setQueuedMessages((currentQueuedMessages) => [
            ...currentQueuedMessages,
            {
                id: `queued_${Date.now()}_${Math.random().toString(36).slice(2)}`,
                content: trimmedMessage,
                queueScopeVersion: queueScopeVersionRef.current,
            },
        ]);
        setMessage('');
        resetTextAreaHeight();
        (_a = textAreaRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [disabled, message, resetTextAreaHeight]);
    const handleDeleteQueuedMessage = useCallback((queuedMessageId) => {
        setQueuedMessages((currentQueuedMessages) => currentQueuedMessages.filter(({ id }) => id !== queuedMessageId));
    }, []);
    const handleTurnRejected = useCallback(() => {
        const rejected = lastSubmittedRef.current;
        lastSubmittedRef.current = null;
        if (!rejected) {
            return;
        }
        dispatch(removeMessage({
            messageId: rejected.tempId,
            conversationId: rejected.conversationId,
        }));
        setMessage((current) => current.trim().length > 0 ? current : rejected.text);
        dispatch(setMessageStreamingError({
            message: 'assistant.turn_in_progress',
            type: 'connection',
            timestamp: new Date().toISOString(),
            dismissible: true,
        }));
    }, [dispatch]);
    useEffect(() => {
        if (!eventProcessor) {
            return;
        }
        eventProcessor.setOnTurnRejected(handleTurnRejected);
        return () => {
            eventProcessor.setOnTurnRejected(null);
        };
    }, [eventProcessor, handleTurnRejected]);
    useEffect(() => {
        if (disabled || assistantIsBusy || queuedMessages.length === 0) {
            return;
        }
        if (queueDrainInFlightRef.current) {
            return;
        }
        const [nextQueuedMessage] = queuedMessages;
        if (!nextQueuedMessage) {
            return;
        }
        if (nextQueuedMessage.queueScopeVersion !== queueScopeVersionRef.current) {
            setQueuedMessages([]);
            return;
        }
        const drainQueueScopeVersion = nextQueuedMessage.queueScopeVersion;
        queueDrainInFlightRef.current = nextQueuedMessage.id;
        setQueuedMessages((currentQueuedMessages) => currentQueuedMessages.filter(({ id }) => id !== nextQueuedMessage.id));
        void (async () => {
            const targetConversationId = await ensureConversationId();
            if (queueScopeVersionRef.current !== drainQueueScopeVersion) {
                return;
            }
            const didSend = await submitAssistantMessage(nextQueuedMessage.content, targetConversationId);
            if (!didSend && queueScopeVersionRef.current === drainQueueScopeVersion) {
                setQueuedMessages((currentQueuedMessages) => [
                    nextQueuedMessage,
                    ...currentQueuedMessages,
                ]);
            }
        })().finally(() => {
            queueDrainInFlightRef.current = null;
        });
    }, [
        assistantIsBusy,
        disabled,
        ensureConversationId,
        queuedMessages,
        submitAssistantMessage,
    ]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (assistantIsBusy) {
                handleQueueMessage();
                return;
            }
            void handleSend();
        }
    }, [assistantIsBusy, handleQueueMessage, handleSend]);
    const handleChange = (e) => {
        setMessage(e.target.value);
    };
    useEffect(() => {
        if (message === '') {
            resetTextAreaHeight();
        }
    }, [message, resetTextAreaHeight]);
    const assistantSetupReady = useMemo(() => {
        return (!!conversationId &&
            !!sendMessageViaSSE &&
            !!onSendWithoutConversation &&
            !!hasNoMessages &&
            status === AssistantStatus.IDLE);
    }, [
        conversationId,
        sendMessageViaSSE,
        onSendWithoutConversation,
        hasNoMessages,
        status,
    ]);
    useEffect(() => {
        if (assistantSetupReady) {
            setIsAutoScrollEnabled(true);
        }
    }, [assistantSetupReady, setIsAutoScrollEnabled]);
    const draftMessage = message.trim();
    const shouldShowQueueContainer = assistantIsBusy || queuedMessages.length > 0;
    const handleSubmit = useCallback(() => {
        if (assistantIsBusy) {
            handleQueueMessage();
            return;
        }
        void handleSend();
    }, [assistantIsBusy, handleQueueMessage, handleSend]);
    const inputContent = (_jsxs(_Fragment, { children: [shouldShowQueueContainer && (_jsx(QueueContainer, { children: _jsxs(AssistantChatInputQueue, { "$gap": theme.spacing.sm, children: [_jsxs(AssistantChatInputQueueHeader, { "$gap": theme.spacing.sm, children: [_jsx(Typography, { elementTheme: "body4", color: theme.colors.grey_2, children: t('assistant|queue_helper', 'The assistant is finishing the current response. You can queue your next message.') }), queuedMessages.length > 0 && (_jsx(Typography, { elementTheme: "overline", color: theme.colors.grey_3, children: `${queuedMessages.length} ${t('assistant|queued_suffix', 'queued')}` }))] }), queuedMessages.map((queuedMessage, index) => (_jsxs(QueueMessageCard, { children: [_jsxs(QueueMessageContent, { children: [_jsx(Typography, { elementTheme: "overline", color: theme.colors.grey_3, children: `${t('assistant|queued_message_label', 'Queued message')} ${index + 1}` }), _jsx(Typography, { elementTheme: "body4", color: theme.colors.grey_2, style: {
                                                overflowWrap: 'anywhere',
                                                wordBreak: 'break-word',
                                            }, children: queuedMessage.content })] }), _jsx(AssistantChatInputQueueMessageActions, { children: _jsx(Button, { transparent: true, size: "circle", defaultStyle: theme.colors.grey_2, headingIcon: _jsx(Delete, { size: 16 }), onClick: () => handleDeleteQueuedMessage(queuedMessage.id) }) })] }, queuedMessage.id)))] }) })), _jsx(ChatInputContainer, { children: _jsxs(InputWrapper, Object.assign({}, getTestId('assistant-input-wrapper'), { children: [_jsx(StyledTextArea, Object.assign({}, getTestId('assistant-input'), { ref: textAreaRef, value: message, onChange: handleChange, onKeyDown: handleKeyDown, placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : defaultPlaceholder, disabled: disabled, rows: 1, autoSize: { maxRows: 5, minRows: 1 } })), _jsxs(AssistantChatInputComposerControls, { children: [isAdmin && !!(usageContext === null || usageContext === void 0 ? void 0 : usageContext.grant_application_id) && (_jsx(AgentModeSelector, { selectedMode: agentMode, onModeChange: setAgentMode, disabled: disabled || assistantIsBusy })), isAdmin && (_jsx(ModelSelector, { selectedModel: selectedModel, onModelChange: (model) => {
                                        setSelectedModel(model);
                                        if (modelAutoSwitch) {
                                            dispatch(clearModelAutoSwitch());
                                        }
                                    }, disabled: disabled || assistantIsBusy, autoSwitchNotice: modelAutoSwitchNotice, onAutoSwitchDismiss: () => dispatch(clearModelAutoSwitch()) })), _jsx(Button, Object.assign({}, getTestId('assistant-submit-button'), { size: "circle", shape: "rounded-corner", headingIcon: _jsx(Arrow, { direction: "up", size: 16 }), onClick: handleSubmit, disabled: disabled || draftMessage === '' }))] })] })) })] }));
    return (_jsxs(ChatInputWrapper, { children: [_jsx(ChatBackground, { "$visible": hasConversation }), inputContent, hasConversation ? (_jsx(Typography, { elementTheme: "overline", color: theme.colors.grey_3, textAlign: "center", style: {
                    marginTop: theme.spacing.md,
                    position: 'relative',
                }, children: t('assistant|disclaimer') })) : null] }));
};
export default AssistantChatInput;
